import React, { useEffect, useState } from "react";
import { IconKeyboardArrowRightBlue, LogoCurrentBlue } from "assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { fetchGetProduct, productCleanData } from "reducers/productSlice";
import { API_ENDPOINTS, FILE_BASE_URL } from "config/apiConfig";
import { Link } from "react-router-dom";
import { getDiffTime, UTCToJapanTime } from "common/helper/time.helper";
import {
  fetchTransactionNotification,
  transactionCleanData,
} from "reducers/transactionSlice";
import {
  transactionNotificationText,
  questNotificationText,
} from "common/helper/text.helper";
import { useGetQuestNotificationsQuery } from "reducers/questApi";
import { useCommonContext } from "contexts/CommonContext";
import { axiosInstance } from "config/axiosConfig";
import { ConversationInquiryStatusEnum } from "common/enums/ConversationInquiryStatus.enum";
import { productPrice } from "common/utils";
import CompanyProfileDashboard from "components/topScreen/CompanyProfileDashboard";

const Dashboard = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { userData } = useSelector((state: RootState) => state.auth);
  const controller = new AbortController();
  const { productLimit } = useSelector((state: RootState) => state.product);
  const { transactionNotificationData } = useSelector(
    (state: RootState) => state.transaction,
  );
  const { data: questNotificationData } = useGetQuestNotificationsQuery();
  const questNotifications = questNotificationData?.data ?? [];
  const [conversionInquiry, setConversationInquiry] = useState<any>([]);

  useEffect(() => {
    fetchConversationInquiry();
    dispatch(
      fetchGetProduct(
        {
          limit: 3,
          sort: "id:desc",
        },
        controller.signal,
      ),
    );

    dispatch(fetchTransactionNotification());
    setHeaderTitle("ダッシュボード");
    return () => {
      dispatch(productCleanData());
      dispatch(transactionCleanData());
    };
  }, []);

  const fetchConversationInquiry = () => {
    axiosInstance
      .get(API_ENDPOINTS.CONVERSATION_INQUIRY, {
        params: {
          limit: 5,
          sort: "id:desc",
          status: ConversationInquiryStatusEnum.PROCESSING,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          setConversationInquiry(response.data.data);
        }
      });
  };

  const formatTime = (time: string) => {
    const timeJapan = UTCToJapanTime(time);
    return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
  };
  return (
    <div className="flex min-h-[924px] p-[20px_20px_100px_20px] md:p-[40px] flex-col items-start gap-[20px] md:gap-[40px] self-stretch bg-white rounded-b-[12px]">
      {/*Logo (Mobile)*/}
      <div className="md:hidden flex w-full h-[67px] justify-center items-center aspect-[180/67]">
        <LogoCurrentBlue className="w-[180px] h-[73px] shrink-0" />
      </div>
      <div className="self-stretch text-black font-['Noto Sans JP'] text-[15px] md:text-[16px] not-italic font-normal leading-[160%]">
        ようこそ、{userData.company_name}さま
      </div>
      <CompanyProfileDashboard companyCode={userData.code} role="seller" />
      {/*Section Wrap*/}
      <div className="flex flex-col items-start gap-[30px] md:gap-[40px] self-stretch">
        {/*Section - Quest achievement notice*/}
        {questNotifications.length > 0 && (
          <div className="flex flex-col items-start self-stretch">
            {/*Title*/}
            <div className="h-[45px] md:h-[55px] flex py-[8px] md:py-[10px] items-center gap-[10px] md:gap-[15px] self-stretch border-b border-neutral-border">
              <div className="w-[4px] self-stretch rounded-[2px] bg-primary"></div>
              <div className="flex items-center gap-[10px] flex-[1_0_0]">
                <div className="flex items-center gap-[5px]">
                  <div className="h-[29px] md:h-[35px] text-primary font-['Noto Sans JP'] text-[18px] md:text-[22px] not-italic font-bold leading-[160%]">
                    クエスト達成のお知らせ
                  </div>
                </div>
              </div>
            </div>
            {/*Contents*/}
            <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
              <div className="flex flex-col items-start self-stretch">
                {questNotifications.map((notice) => (
                  <div
                    key={notice.company_quest_id}
                    className="md:h-[42px] flex py-[10px] justify-center items-center gap-[10px] self-stretch border-b border-neutral-border bg-white"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] md:gap-[10px] flex-[1_0_0]">
                      <div className="md:max-h-[22px] md:h-[22px] flex-[1_0_0] text-black font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                        {questNotificationText(notice)}
                      </div>
                      <div className="h-[18px] md:h-[19px] text-primary text-left font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
                        {getDiffTime(notice.claimed_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/*Section*/}
        <div className="flex flex-col items-start self-stretch">
          {/*Title*/}
          <div className="h-[45px] md:h-[55px] flex py-[8px] md:py-[10px] items-center gap-[10px] md:gap-[15px] self-stretch border-b border-neutral-border">
            <div className="w-[4px] self-stretch rounded-[2px] bg-primary"></div>
            <div className="flex items-center gap-[10px] flex-[1_0_0]">
              <div className="flex items-center gap-[5px]">
                <div className="h-[29px] md:h-[35px] text-primary font-['Noto Sans JP'] text-[18px] md:text-[22px] not-italic font-bold leading-[160%]">
                  取引についてのお知らせ
                </div>
              </div>
            </div>
          </div>
          {/*Contents*/}
          <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
            <div className="flex flex-col items-start self-stretch">
              {/*Items*/}

              {transactionNotificationData?.data.length === 0 ? (
                <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                  <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                    出品中の商品がありません
                  </div>
                </div>
              ) : (
                transactionNotificationData?.data.map((transaction, index) => {
                  return (
                    <Link
                      to={`/seller/product/${transaction.product.code}/transaction/${transaction.buyer_id}/${transaction.seller_id}`}
                      key={index}
                      className="h-[89px] md:h-[42px] flex py-[10px] justify-center items-center gap-[10px] self-stretch border-b border-neutral-border bg-white"
                    >
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] md:gap-[10px] flex-[1_0_0]">
                        <div className="max-h-[44px] md:max-h-[22px] md:h-[22px] flex-[1_0_0] text-black font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                          {transactionNotificationText(transaction)}
                        </div>
                        <div className="h-[18px] md:h-[19px] text-primary text-left font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
                          {getDiffTime(transaction.commonDate)}
                        </div>
                      </div>
                      <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </div>
        {/*Section*/}
        <div className="flex flex-col items-start self-stretch">
          {/*Title*/}
          <div className="h-[45px] md:h-[55px] flex py-[8px] md:py-[10px] items-center gap-[10px] md:gap-[15px] self-stretch border-b border-neutral-border">
            <div className="w-[4px] self-stretch rounded-[2px] bg-primary"></div>
            <div className="flex items-center gap-[10px] flex-[1_0_0]">
              <div className="flex items-center gap-[5px]">
                <div className="h-[29px] md:h-[35px] text-primary font-['Noto Sans JP'] text-[18px] md:text-[22px] not-italic font-bold leading-[160%]">
                  出品中の商品
                </div>
              </div>
            </div>
            <div className="hidden md:flex items-center">
              <Link
                to="/seller/list"
                className="h-[22px] text-primary font-['Noto Sans JP'] text-[14px] not-italic font-medium leading-[160%] underline decoration-solid"
              >
                全ての出品中の商品を見る
              </Link>
              <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
            </div>
          </div>
          {/*Contents*/}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-[10px] md:gap-[20px] self-stretch">
            <div className="w-full flex flex-col justify-center items-start flex-[1_0_0]">
              {productLimit?.data.length === 0 ? (
                <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                  <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                    出品中の商品がありません
                  </div>
                </div>
              ) : (
                productLimit?.data.map((product: any) => (
                  <Link
                    to={`/seller/product/${product.code}`}
                    key={product.id}
                    className="md:h-[110px] flex py-[10px] md:py-[15px] items-center gap-[5px] md:gap-[20px] self-stretch border-b border-neutral-border bg-white cursor-pointer"
                  >
                    <div className="flex items-center gap-[15px] md:gap-[20px] flex-[1_0_0]">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={`${FILE_BASE_URL}/${product.images[0].image_url}`}
                          alt={product.product_name}
                          className="w-[60px] h-[60px] md:w-[80px] md:h-[80px] rounded-[6px] object-cover"
                        />
                      ) : (
                        <div className="w-[60px] h-[60px] md:w-[80px] md:h-[80px] rounded-[6px] bg-primary-light"></div>
                      )}
                      <div className="flex flex-col justify-center items-start gap-[5px] flex-[1_0_0]">
                        <div className="md:h-[29px] self-stretch text-black font-['Noto Sans JP'] text-[15px] md:text-[18px] not-italic font-normal leading-[160%] truncate whitespace-break-spaces">
                          {product.product_name}
                        </div>
                        <div className="h-[18px] md:h-[19px] self-stretch text-black font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-bold leading-[160%]">
                          {productPrice(product)}
                        </div>
                      </div>
                    </div>
                    <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                  </Link>
                ))
              )}
            </div>
            <div className="md:hidden flex items-center">
              <Link
                to="/seller/list"
                className="text-primary font-['Noto Sans JP'] text-[14px] not-italic font-medium leading-[160%] underline decoration-solid"
              >
                全ての出品中の商品を見る
              </Link>
              <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
            </div>
          </div>
        </div>
        {/*Section*/}
        <div className="flex flex-col items-start self-stretch">
          {/*Title*/}
          <div className="h-[45px] md:h-[55px] flex py-[8px] md:py-[10px] items-center gap-[10px] md:gap-[15px] self-stretch border-b border-neutral-border">
            <div className="w-[4px] self-stretch rounded-[2px] bg-primary"></div>
            <div className="flex items-center gap-[10px] flex-[1_0_0]">
              <div className="flex items-center gap-[5px]">
                <div className="h-[29px] md:h-[35px] text-primary font-['Noto Sans JP'] text-[18px] md:text-[22px] not-italic font-bold leading-[160%]">
                  お知らせ
                </div>
              </div>
            </div>
          </div>
          {/*Contents*/}
          <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
            <div className="flex flex-col items-start self-stretch">
              {conversionInquiry.length === 0 ? (
                <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                  <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                    表示する内容がありません
                  </div>
                </div>
              ) : (
                ""
              )}

              {conversionInquiry.map((item: any) => (
                <Link
                  to="/seller/conversation-inquiry"
                  key={item.id}
                  className="self-stretch flex flex-col justify-start items-start"
                >
                  <div
                    data-プロパティ1="base"
                    className="self-stretch py-2.5 bg-white border-b border-neutral-300 inline-flex justify-center items-center gap-2.5"
                  >
                    <div className="flex-1 flex flex-col md:flex-row justify-start items-start md:items-center gap-[5px] md:gap-16">
                      <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                        管理者があなたのトピック「{item.subject}」に返信しました
                      </div>
                      <div className="text-right justify-start text-blue-900 text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
                        {formatTime(item.created_at)}
                      </div>
                    </div>
                    <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
